import React, { useState, useContext } from 'react';
import { useNavigate, Navigate } from 'react-router-dom'
import EmailValidator from 'email-validator';
import { makeStyles } from '@material-ui/core';
import {
    Typography,
    Box,
    Button,
    Divider,
    TextField,
    InputAdornment,
    IconButton,
    Link,
    Alert,
    Backdrop,
    CircularProgress
} from '@mui/material';

import GoogleIcon from '@mui/icons-material/Google';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LoadingButton from '@mui/lab/LoadingButton';

import { registerWithEmail, registerWithGoogle } from '../services/Authentication';
import { AuthContext } from '../components/authentication/Auth';


const useStyle = makeStyles(theme => {
    return {
        container: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            marginTop: 20,
            marginBottom: 50,
            maxWidth: 450,
            margin: 'auto',
        },
        title: {
            textAlign: 'left',
            width: '100%',
        },
        button: {
            width: '100%',
            height: 80,
            fontWeight: 'bold',
            fontSize: 90,
        },
    }
})

const SignUp = () => {
    const classes = useStyle()
    const navigate = useNavigate()

    const { currentUser } = useContext(AuthContext);


    const [errorMessage, setErrorMessage] = useState('');

    const [email, setEmail] = useState('')
    const [emailInputErrorMessage, setEmailInputErrorMessage] = useState('');

    const [password, setPassword] = useState('')
    const [passwordInputErrorMessage, setPasswordInputErrorMessage] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    const [formIsValid, setFormIsValid] = useState(false);
    const [buttonLoader, setButtonLoader] = useState(false);
    const [screenLoader, setScreenLoader] = useState(false);

    const emailError = emailInputErrorMessage.length !== 0 ? true : false;
    const passwordError = passwordInputErrorMessage.length !== 0 ? true : false;

    const handleCheckEmailValidity = () => {
        if (!EmailValidator.validate(email) && email.length !== 0) {
            setEmailInputErrorMessage('E-mail invalide.')
        } else {
            setEmailInputErrorMessage('')
        }
    }

    const handleCheckPasswordValidity = () => {
        if (password.length !== 0 && password.length < 6 || password.length > 100) {
            setPasswordInputErrorMessage('Le mot de passe doit faire entre 6 et 100 caractères.')
        } else {
            setPasswordInputErrorMessage('')
        }
    }

    const handleCheckFormValidity = () => {
        if (password.length >= 6 && password.length <= 100 && EmailValidator.validate(email)) {
            setFormIsValid(true)
        } else {
            setFormIsValid(false)
        }
    }

    const handleFormSubmit = () => {
        setErrorMessage('')
        setButtonLoader(true)
        registerWithEmail(email, password)
            .then(() => {
                navigate('/')
            })
            .catch(error => {
                setButtonLoader(false)
                createErrorMessage(error.code)
            })
    }

    const handleRegisterWithGoogle = () => {
        setErrorMessage('')
        setScreenLoader(true)
        registerWithGoogle()
            .then(() => {
                navigate('/')
            })
            .catch(error => {
                setScreenLoader(false)
                createErrorMessage(error)
            })
    }

    const createErrorMessage = (errorMessage) => {
        switch (errorMessage) {
            case 'auth/email-already-in-use':
                setErrorMessage('Utilisateur déjà existant.')
                break;
            case 'Utilisateur déjà existant':
                setErrorMessage('Utilisateur déjà existant.')
                break;
            default:
                setErrorMessage('Une erreur est survenue, merci de réessayer.')
                break;
        }
    }

    // If user is auth, redirect to 'AllProjects' screen
    if (currentUser) {
        return <Navigate to="/" />
    }

    return (
        <Box padding={2} >
            <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={screenLoader}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
            <Typography fontSize={36} fontWeight='bold' >Spliit</Typography>

            <Box className={classes.container}>
                <Typography variant='h1' sx={{ marginBottom: 4, fontSize: { xs: 40, md: 48 } }} fontWeight='bold' className={classes.title}>
                    Créer un compte
                </Typography>
                <Button onClick={() => handleRegisterWithGoogle()} className={classes.button} startIcon={<GoogleIcon />} color='primary' variant='outlined'>
                    <Typography>
                        Continuer avec Google
                    </Typography>
                </Button>
                <Divider sx={{ width: '100%', marginY: 4 }}>Ou</Divider>
                {errorMessage.length !== 0 ?
                    <Alert severity="error" sx={{ marginBottom: 3, alignSelf: 'flex-start' }}>
                        {errorMessage}
                    </Alert>
                    : null
                }
                <form>
                    <TextField
                        autoFocus
                        type='email'
                        sx={{ m: 1, width: '100%' }}
                        value={email} label='E-mail'
                        onChange={e => setEmail(e.target.value)}
                        onBlur={() => {
                            handleCheckEmailValidity()
                            handleCheckFormValidity()
                        }}
                        error={emailError}
                        helperText={emailInputErrorMessage}
                    />
                    <TextField
                        sx={{ m: 1, width: '100%' }}
                        helperText={passwordInputErrorMessage}
                        error={passwordError}
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        onBlur={() => {
                            handleCheckPasswordValidity()
                            handleCheckFormValidity()
                        }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={() => setShowPassword(!showPassword)}
                                        edge="end"
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                        label="Mot de passe"
                    />
                    <LoadingButton
                        loading={buttonLoader}
                        disabled={!formIsValid}
                        sx={{ width: '100%', height: 56, marginTop: 4 }}
                        variant='contained'
                        onClick={() => handleFormSubmit()}
                        type='submit'
                    >
                        Valider
                    </LoadingButton>
                </form>
                <Link href='/login' sx={{ marginTop: 2, textAlign: 'left', width: '100%' }}>
                    Vous avez déjà un compte ?
                </Link>
            </Box>
        </Box>
    )
};

export default SignUp;



